(() => {
  const ROW_TOLERANCE = 4;
  const CARD_STAGGER_MS = 120;

  const getGalleryNavigationMode = (gallery) => {
    if (!gallery?.id) return null;
    if (window.location.hash === `#${gallery.id}`) return 'hash';

    const requestedTarget = new URLSearchParams(window.location.search).get('scroll_to');
    return requestedTarget === gallery.id ? 'smooth' : null;
  };

  const alignGalleryBelowStickyHeader = (gallery, behavior = 'auto') => {
    if (!getGalleryNavigationMode(gallery)) return;

    const stickyHeader = document.getElementById('theme-header');
    let stickyHeaderBottom = 0;

    if (stickyHeader) {
      const wasSticky = stickyHeader.classList.contains('header-sticky');

      if (!wasSticky) stickyHeader.classList.add('header-sticky');
      stickyHeaderBottom = Math.max(0, stickyHeader.getBoundingClientRect().bottom);
      if (!wasSticky) stickyHeader.classList.remove('header-sticky');
    }

    const targetTop = gallery.getBoundingClientRect().top + window.scrollY - stickyHeaderBottom;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: reduceMotion ? 'auto' : behavior,
    });
  };

  const scheduleAnchorAlignment = (gallery) => {
    const navigationMode = getGalleryNavigationMode(gallery);
    if (!navigationMode) return;

    const behavior = navigationMode === 'smooth' ? 'smooth' : 'auto';

    const align = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => alignGalleryBelowStickyHeader(gallery, behavior));
      });
    };

    if (navigationMode === 'smooth' && document.readyState !== 'complete') {
      window.addEventListener('load', align, { once: true });
    } else {
      align();
    }
  };

  const updateCardRevealDelays = (cards) => {
    let row = -1;
    let rowTop = null;
    let positionInRow = 0;

    cards.filter((card) => !card.hidden).forEach((card) => {
      const cardTop = Math.round(card.offsetTop);

      if (rowTop === null || Math.abs(cardTop - rowTop) > ROW_TOLERANCE) {
        row += 1;
        rowTop = cardTop;
        positionInRow = 0;
      }

      card.dataset.dppRevealRow = String(row);
      card.dataset.dppRevealPosition = String(positionInRow);
      card.style.setProperty(
        '--dpp-build-card-reveal-delay',
        `${positionInRow * CARD_STAGGER_MS}ms`,
      );
      positionInRow += 1;
    });
  };

  const initializeGallery = (gallery) => {
    if (!gallery || gallery.dataset.galleryReady === 'true') return;

    const filter = gallery.querySelector('[data-build-filter]');
    let cards = Array.from(gallery.querySelectorAll('[data-build-card]'));
    const count = gallery.querySelector('[data-build-count]');
    const empty = gallery.querySelector('[data-build-empty]');

    gallery.dataset.galleryReady = 'true';
    scheduleAnchorAlignment(gallery);
    if (count) count.textContent = `${cards.length} ${cards.length === 1 ? 'BUILD' : 'BUILDS'}`;

    if (!cards.length) {
      const filterControl = filter?.closest('.dpp-build-gallery__filter');
      if (filterControl) filterControl.hidden = true;
      if (empty) empty.hidden = false;
      return;
    }

    const orderedCards = cards.map((card, index) => {
      const rawOrder = card.dataset.buildOrder?.trim() || '';
      const parsedOrder = rawOrder === '' ? null : Number(rawOrder);

      return {
        card,
        index,
        order: Number.isFinite(parsedOrder) ? parsedOrder : null,
      };
    });

    if (orderedCards.some(({ order }) => order !== null)) {
      orderedCards.sort((first, second) => {
        const firstIsOrdered = first.order !== null;
        const secondIsOrdered = second.order !== null;

        if (firstIsOrdered && secondIsOrdered) {
          return first.order - second.order || first.index - second.index;
        }

        if (firstIsOrdered) return -1;
        if (secondIsOrdered) return 1;
        return first.index - second.index;
      });

      const grid = gallery.querySelector('.dpp-build-gallery__grid');
      if (grid) {
        const fragment = document.createDocumentFragment();
        orderedCards.forEach(({ card }) => fragment.append(card));
        grid.append(fragment);
        cards = orderedCards.map(({ card }) => card);
      }
    }

    let revealDelayFrame = 0;
    const scheduleRevealDelays = () => {
      if (revealDelayFrame) cancelAnimationFrame(revealDelayFrame);
      revealDelayFrame = requestAnimationFrame(() => {
        revealDelayFrame = 0;
        updateCardRevealDelays(cards);
      });
    };

    scheduleRevealDelays();

    const grid = gallery.querySelector('.dpp-build-gallery__grid');
    if (grid && 'ResizeObserver' in window) {
      const revealResizeObserver = new ResizeObserver(scheduleRevealDelays);
      revealResizeObserver.observe(grid);
      gallery.dppRevealResizeObserver = revealResizeObserver;
    }

    if (!filter) return;

    const makes = [...new Set(cards.map((card) => card.dataset.buildMake).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));

    if (!makes.length) {
      const filterControl = filter.closest(".dpp-build-gallery__filter");
      if (filterControl) filterControl.hidden = true;
      return;
    }

    makes.forEach((make) => {
      const option = document.createElement('option');
      option.value = make;
      option.textContent = make.toUpperCase();
      filter.append(option);
    });

    filter.addEventListener('change', () => {
      const selected = filter.value;
      let visible = 0;

      cards.forEach((card) => {
        const show = !selected || card.dataset.buildMake === selected;
        card.hidden = !show;
        if (show) visible += 1;
      });

      if (count) count.textContent = `${visible} ${visible === 1 ? 'BUILD' : 'BUILDS'}`;
      if (empty) empty.hidden = visible !== 0;
      scheduleRevealDelays();
    });
  };

  const initializeWithin = (root = document) => {
    if (root.matches?.('[data-dpp-build-gallery]')) initializeGallery(root);
    root.querySelectorAll?.('[data-dpp-build-gallery]').forEach(initializeGallery);
  };

  initializeWithin();

  document.addEventListener('shopify:section:load', (event) => {
    initializeWithin(event.target);
  });
})();
